import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  ArrowLeft, Building2, Truck, KeyRound, Wallet, FileText, Map, History,
  Loader2, Plus, Trash2, Copy, RotateCcw, Lock, Unlock, CheckCircle2, AlertTriangle, Search,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_PILL = {
  ativo: "bg-emerald-100 text-emerald-800 border-emerald-300",
  ativa: "bg-emerald-100 text-emerald-800 border-emerald-300",
  inativo: "bg-zinc-100 text-zinc-700 border-zinc-300",
  sem_acesso: "bg-zinc-100 text-zinc-700 border-zinc-300",
  sem_contrato: "bg-blue-100 text-blue-800 border-blue-300",
  sem_frota: "bg-zinc-100 text-zinc-700 border-zinc-300",
  sem_movimento: "bg-zinc-100 text-zinc-700 border-zinc-300",
  regular: "bg-emerald-100 text-emerald-800 border-emerald-300",
  pendente: "bg-amber-100 text-amber-800 border-amber-300",
  suspensa: "bg-red-100 text-red-800 border-red-300",
  inadimplente: "bg-red-100 text-red-800 border-red-300",
  em_negociacao: "bg-amber-100 text-amber-800 border-amber-300",
};
const StatusBadge = ({ value }) => (
  <Badge className={`${STATUS_PILL[value] || "bg-zinc-100 text-zinc-700"} border text-xs`}>
    {String(value || "—").replace(/_/g, " ")}
  </Badge>
);

const fmtMoney = (v) => (v == null ? "—" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));

export default function ClienteCentral360() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [central, setCentral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/clientes/${id}/central`);
      setCentral(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar central do cliente");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !central) {
    return (
      <div className="p-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="animate-spin" size={18} /> Carregando central do cliente…
      </div>
    );
  }

  const { dados, kpis, status, portal_user } = central;

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="cliente-central-360">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost" size="sm" onClick={() => navigate("/app/admin/clientes")}
            data-testid="btn-voltar-admin-clientes"
          >
            <ArrowLeft size={16} className="mr-1" /> Voltar
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Building2 size={20} className="text-orange-600" />
              <h1 className="text-2xl font-bold tracking-tight" data-testid="cliente-nome">
                {dados.nome}
              </h1>
              <Badge variant="outline" className="text-xs">
                {central.origem === "locativo" ? "Base mestra" : "CRM"}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {dados.cnpj || "Sem CNPJ"} · {dados.cidade || "—"}/{dados.uf || "—"} · ID {id}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusBadge value={status.cadastro} />
          <StatusBadge value={status.portal} />
          <StatusBadge value={status.contrato} />
          <StatusBadge value={status.frota} />
          <StatusBadge value={status.financeiro_detalhe} />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Frota ativa" value={kpis.frota_count} icon={<Truck size={16} />} testid="kpi-frota" />
        <KPI label="Contratos ativos" value={kpis.contratos_ativos} icon={<FileText size={16} />} testid="kpi-contratos-ativos" />
        <KPI label="Contratos totais" value={kpis.contratos_total} icon={<FileText size={16} />} testid="kpi-contratos-total" />
        <KPI label="Propostas" value={kpis.propostas_total} icon={<Wallet size={16} />} testid="kpi-propostas" />
        <KPI label="Portal" value={kpis.portal_criado ? "Sim" : "Não"} icon={<KeyRound size={16} />} testid="kpi-portal" />
      </div>

      {/* TABS */}
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="grid grid-cols-3 md:grid-cols-7 gap-1 h-auto p-1">
          <TabsTrigger value="overview" data-testid="tab-overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="frota" data-testid="tab-frota">Frota</TabsTrigger>
          <TabsTrigger value="portal" data-testid="tab-portal">Portal</TabsTrigger>
          <TabsTrigger value="financeiro" data-testid="tab-financeiro">Financeiro</TabsTrigger>
          <TabsTrigger value="propostas" data-testid="tab-propostas">Propostas</TabsTrigger>
          <TabsTrigger value="operacoes" data-testid="tab-operacoes">Operações</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico">Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><TabOverview central={central} /></TabsContent>
        <TabsContent value="frota"><TabFrota adminId={id} onChanged={carregar} /></TabsContent>
        <TabsContent value="portal"><TabPortal adminId={id} initialUser={portal_user} onChanged={carregar} /></TabsContent>
        <TabsContent value="financeiro"><TabFinanceiro adminId={id} /></TabsContent>
        <TabsContent value="propostas"><TabPropostas adminId={id} /></TabsContent>
        <TabsContent value="operacoes"><TabOperacoes nomeCliente={dados.nome} /></TabsContent>
        <TabsContent value="historico"><TabHistorico adminId={id} /></TabsContent>
      </Tabs>
    </div>
  );
}

const KPI = ({ label, value, icon, testid }) => (
  <Card className="p-3" data-testid={testid}>
    <div className="flex items-center gap-2 text-muted-foreground text-xs">
      {icon} {label}
    </div>
    <div className="text-2xl font-bold mt-1">{value}</div>
  </Card>
);

// ============================================================
// 1. VISÃO GERAL
// ============================================================
function TabOverview({ central }) {
  const { dados, status, ultima_movimentacao, portal_user } = central;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Building2 size={16} /> Dados cadastrais
        </h3>
        <dl className="text-sm space-y-2">
          <Row k="Razão social" v={dados.razao_social} />
          <Row k="CNPJ" v={dados.cnpj || "—"} />
          <Row k="IE" v={dados.ie || "—"} />
          <Row k="Endereço" v={dados.endereco_completo || "—"} />
          <Row k="Cidade/UF" v={`${dados.cidade || "—"} / ${dados.uf || "—"}`} />
          <Row k="Telefone" v={dados.telefone || "—"} />
          <Row k="E-mail" v={dados.email || "—"} />
          <Row k="Tipo" v={dados.tipo || "—"} />
          <Row k="Criado em" v={dados.criado_em || "—"} />
        </dl>
      </Card>

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">Status consolidados</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <StatusLine label="Cadastro" value={status.cadastro} />
          <StatusLine label="Portal" value={status.portal} />
          <StatusLine label="Contrato" value={status.contrato} />
          <StatusLine label="Frota" value={status.frota} />
          <StatusLine label="Operacional" value={status.operacional} />
          <StatusLine label="Financeiro" value={status.financeiro_detalhe} />
        </div>
        <div className="mt-4 pt-4 border-t">
          <div className="text-xs font-semibold text-muted-foreground mb-1">Portal</div>
          {portal_user ? (
            <div className="text-sm">
              <div>{portal_user.nome || portal_user.email}</div>
              <div className="text-xs text-muted-foreground">{portal_user.email}</div>
              <div className="text-xs text-muted-foreground mt-1">
                Último acesso: {portal_user.ultimo_acesso ? new Date(portal_user.ultimo_acesso).toLocaleString("pt-BR") : "nunca"}
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground italic">Sem acesso ao portal cadastrado.</div>
          )}
        </div>
        <div className="mt-4 pt-4 border-t">
          <div className="text-xs font-semibold text-muted-foreground mb-1">Última movimentação</div>
          <div className="text-sm">
            {ultima_movimentacao
              ? `${ultima_movimentacao.acao} em ${new Date(ultima_movimentacao.criado_em).toLocaleString("pt-BR")}`
              : <span className="text-muted-foreground italic">Sem registros</span>}
          </div>
        </div>
      </Card>
    </div>
  );
}
const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3 border-b border-dashed last:border-0 pb-1">
    <dt className="text-muted-foreground">{k}</dt>
    <dd className="font-medium text-right">{v}</dd>
  </div>
);
const StatusLine = ({ label, value }) => (
  <div className="flex justify-between items-center">
    <span className="text-muted-foreground">{label}</span>
    <StatusBadge value={value} />
  </div>
);

// ============================================================
// 2. FROTA DO CLIENTE
// ============================================================
function TabFrota({ adminId, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAssoc, setOpenAssoc] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/clientes/${adminId}/frota`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar frota");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [adminId]);

  const remover = async (eq_id) => {
    if (!window.confirm("Remover este equipamento da frota do cliente?")) return;
    try {
      await api.delete(`/admin/clientes/${adminId}/frota/${eq_id}`);
      toast.success("Equipamento desvinculado");
      carregar(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover");
    }
  };

  return (
    <Card className="p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Truck size={16} /> Frota vinculada ({items.length})
        </h3>
        <Button onClick={() => setOpenAssoc(true)} data-testid="btn-associar-equipamento" size="sm">
          <Plus size={14} className="mr-1" /> Associar equipamento
        </Button>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm flex items-center gap-2">
          <Loader2 className="animate-spin" size={14} /> Carregando…
        </div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground italic py-6 text-center border border-dashed rounded">
          Nenhum equipamento vinculado.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Serial</TableHead>
              <TableHead>Placa</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Propriedade</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((e) => (
              <TableRow key={e.id} data-testid={`frota-row-${e.id}`}>
                <TableCell className="font-mono text-xs">{e.id}</TableCell>
                <TableCell className="font-mono text-xs">{e.serial}</TableCell>
                <TableCell>{e.placa || "—"}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{e.status || "—"}</Badge></TableCell>
                <TableCell className="text-xs">{e.propriedade || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => remover(e.id)} data-testid={`frota-remover-${e.id}`}>
                    <Trash2 size={14} className="text-red-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {openAssoc && (
        <DialogAssociar
          adminId={adminId}
          onClose={() => setOpenAssoc(false)}
          onSaved={() => { setOpenAssoc(false); carregar(); onChanged?.(); }}
        />
      )}
    </Card>
  );
}

function DialogAssociar({ adminId, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const buscar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/clientes/${adminId}/equipamentos-disponiveis`, {
        params: { q, limit: 30 },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro na busca");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { const t = setTimeout(buscar, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const toggle = (id) => {
    const novo = new Set(sel);
    if (novo.has(id)) novo.delete(id); else novo.add(id);
    setSel(novo);
  };

  const associar = async () => {
    if (sel.size === 0) { toast.error("Selecione ao menos 1 equipamento"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/clientes/${adminId}/frota`, {
        equipamento_ids: Array.from(sel),
        motivo: "Associação via Central 360",
      });
      toast.success(`${data.vinculados} equipamento(s) vinculado(s)`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao associar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="dialog-associar-frota">
        <DialogHeader>
          <DialogTitle>Associar equipamentos à frota</DialogTitle>
          <DialogDescription>
            Busque por serial, placa ou propriedade. Apenas equipamentos disponíveis aparecem.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-muted-foreground" />
            <Input
              className="pl-9" placeholder="Buscar equipamento…" value={q}
              onChange={(e) => setQ(e.target.value)} data-testid="input-busca-equipamento"
            />
          </div>
          <div className="border rounded max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="animate-spin" size={14} /> Buscando…
              </div>
            ) : items.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground italic">Nenhum equipamento disponível</div>
            ) : (
              <ul className="divide-y">
                {items.map((e) => (
                  <li key={e.id}
                    className={`p-2 text-sm cursor-pointer hover:bg-zinc-50 flex items-center gap-2 ${sel.has(e.id) ? "bg-orange-50" : ""}`}
                    onClick={() => toggle(e.id)} data-testid={`equip-disp-${e.id}`}>
                    <input type="checkbox" checked={sel.has(e.id)} readOnly />
                    <div className="flex-1">
                      <div className="font-mono text-xs">{e.serial} · placa {e.placa || "—"}</div>
                      <div className="text-xs text-muted-foreground">{e.propriedade} · {e.status}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="text-xs text-muted-foreground">{sel.size} selecionado(s)</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={associar} disabled={saving || sel.size === 0} data-testid="btn-confirmar-associar">
            {saving && <Loader2 className="animate-spin mr-2" size={14} />}
            Associar {sel.size > 0 ? `(${sel.size})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// 3. PORTAL
// ============================================================
function TabPortal({ adminId, initialUser, onChanged }) {
  const [items, setItems] = useState(initialUser ? [initialUser] : []);
  const [loading, setLoading] = useState(true);
  const [openCriar, setOpenCriar] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/clientes/${adminId}/portal-users`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [adminId]);

  const reset = async (uid) => {
    if (!window.confirm("Gerar nova senha temporária para este acesso?")) return;
    try {
      const { data } = await api.patch(`/admin/clientes/${adminId}/portal-users/${uid}/reset-senha`);
      window.prompt("Nova senha temporária (envie ao cliente):", data.nova_senha);
      toast.success("Senha resetada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };
  const bloquear = async (uid, ativo) => {
    try {
      await api.patch(`/admin/clientes/${adminId}/portal-users/${uid}/bloquear`, null, { params: { ativo: !ativo } });
      toast.success(ativo ? "Acesso bloqueado" : "Acesso reativado");
      carregar(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  return (
    <Card className="p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <KeyRound size={16} /> Acesso ao Portal do Cliente ({items.length})
        </h3>
        <Button onClick={() => setOpenCriar(true)} size="sm" data-testid="btn-criar-portal-user">
          <Plus size={14} className="mr-1" /> Criar acesso
        </Button>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm flex items-center gap-2">
          <Loader2 className="animate-spin" size={14} /> Carregando…
        </div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground italic py-6 text-center border border-dashed rounded">
          Nenhum usuário do portal criado para este cliente.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Último acesso</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((u) => (
              <TableRow key={u.id} data-testid={`portal-row-${u.id}`}>
                <TableCell className="font-medium">{u.nome}</TableCell>
                <TableCell className="text-xs">{u.email}</TableCell>
                <TableCell className="text-xs">{u.telefone || "—"}</TableCell>
                <TableCell>
                  {u.ativo
                    ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border text-xs">Ativo</Badge>
                    : <Badge className="bg-red-100 text-red-800 border-red-300 border text-xs">Bloqueado</Badge>}
                  {u.exigir_troca_senha && <Badge className="ml-1 bg-amber-100 text-amber-800 text-xs">Pendente troca</Badge>}
                </TableCell>
                <TableCell className="text-xs">
                  {u.ultimo_acesso ? new Date(u.ultimo_acesso).toLocaleString("pt-BR") : "nunca"}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => reset(u.id)} data-testid={`portal-reset-${u.id}`}>
                    <RotateCcw size={14} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => bloquear(u.id, u.ativo)} data-testid={`portal-bloquear-${u.id}`}>
                    {u.ativo ? <Lock size={14} className="text-red-600" /> : <Unlock size={14} className="text-emerald-600" />}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {openCriar && (
        <DialogCriarPortal
          adminId={adminId}
          onClose={() => setOpenCriar(false)}
          onSaved={() => { setOpenCriar(false); carregar(); onChanged?.(); }}
        />
      )}
    </Card>
  );
}

function DialogCriarPortal({ adminId, onClose, onSaved }) {
  const [form, setForm] = useState({ nome: "", email: "", telefone: "", senha_temporaria: "" });
  const [saving, setSaving] = useState(false);
  const [senhaGerada, setSenhaGerada] = useState(null);

  const submit = async () => {
    if (!form.nome || !form.email) { toast.error("Preencha nome e e-mail"); return; }
    setSaving(true);
    try {
      const payload = {
        nome: form.nome, email: form.email,
        telefone: form.telefone || null,
        senha_temporaria: form.senha_temporaria || null,
      };
      const { data } = await api.post(`/admin/clientes/${adminId}/portal-users`, payload);
      setSenhaGerada(data.senha_temporaria);
      toast.success("Acesso criado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar acesso");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="dialog-criar-portal">
        <DialogHeader>
          <DialogTitle>Criar acesso ao portal</DialogTitle>
          <DialogDescription>
            Define nome + e-mail + (opcional) senha. Se deixar em branco, geramos uma temporária.
          </DialogDescription>
        </DialogHeader>
        {senhaGerada ? (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
              <div className="flex items-center gap-2 font-semibold mb-1">
                <CheckCircle2 size={16} className="text-emerald-700" /> Acesso criado
              </div>
              <div>Envie esta senha temporária ao cliente:</div>
              <div className="mt-2 flex items-center gap-2">
                <code className="bg-white px-2 py-1 rounded border font-mono text-sm flex-1" data-testid="senha-gerada">
                  {senhaGerada}
                </code>
                <Button size="sm" variant="outline"
                  onClick={() => { navigator.clipboard.writeText(senhaGerada); toast.success("Copiado!"); }}>
                  <Copy size={14} />
                </Button>
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                <AlertTriangle size={12} className="inline mr-1" />
                O cliente será obrigado a trocar a senha no primeiro login.
              </div>
            </div>
            <DialogFooter>
              <Button onClick={onSaved} data-testid="btn-portal-criado-ok">OK</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <div>
                <Label>Nome *</Label>
                <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="input-portal-nome" />
              </div>
              <div>
                <Label>E-mail *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-portal-email" />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} data-testid="input-portal-telefone" />
              </div>
              <div>
                <Label>Senha temporária (opcional)</Label>
                <Input value={form.senha_temporaria} onChange={(e) => setForm({ ...form, senha_temporaria: e.target.value })} placeholder="Em branco = gerar automaticamente" data-testid="input-portal-senha" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
              <Button onClick={submit} disabled={saving} data-testid="btn-confirmar-portal">
                {saving && <Loader2 className="animate-spin mr-2" size={14} />} Criar acesso
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// 4. FINANCEIRO
// ============================================================
function TabFinanceiro({ adminId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/clientes/${adminId}/financeiro-resumo`);
        setData(data);
      } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
      finally { setLoading(false); }
    })();
  }, [adminId]);

  if (loading) return <Card className="p-6 text-muted-foreground flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Carregando…</Card>;
  if (!data) return null;
  const { kpis, contratos } = data;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Contratos" value={kpis.qtd_contratos} icon={<FileText size={16} />} testid="fin-kpi-qtd" />
        <KPI label="Valor 1ª faturas" value={fmtMoney(kpis.valor_total_primeiras_faturas)} icon={<Wallet size={16} />} testid="fin-kpi-valor" />
        <KPI label="Total bônus" value={fmtMoney(kpis.valor_total_bonus)} icon={<Wallet size={16} />} testid="fin-kpi-bonus" />
        <KPI label="Pendentes assinatura" value={kpis.pendentes_assinatura} icon={<AlertTriangle size={16} />} testid="fin-kpi-pend" />
      </div>
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">Contratos vinculados</h3>
        {contratos.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-6 text-center border border-dashed rounded">
            Nenhum contrato encontrado.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>1ª fatura</TableHead>
                <TableHead>Bônus</TableHead>
                <TableHead>Assinatura</TableHead>
                <TableHead>Início</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contratos.map((c) => (
                <TableRow key={c.id} data-testid={`fin-row-${c.id}`}>
                  <TableCell className="font-mono text-xs">{c.id?.slice(0, 12)}…</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{c.status || "—"}</Badge></TableCell>
                  <TableCell>{fmtMoney(c.valor_primeira_fatura)}</TableCell>
                  <TableCell>{fmtMoney(c.valor_bonus)}</TableCell>
                  <TableCell>
                    {c.signature_status === "pending"
                      ? <Badge className="bg-amber-100 text-amber-800 border-amber-300 border text-xs">Pendente</Badge>
                      : <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border text-xs">OK</Badge>}
                  </TableCell>
                  <TableCell className="text-xs">{c.data_inicio ? String(c.data_inicio).slice(0, 10) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// ============================================================
// 5. PROPOSTAS E CONTRATOS
// ============================================================
function TabPropostas({ adminId }) {
  const [propostas, setPropostas] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [p, c] = await Promise.all([
          api.get(`/admin/clientes/${adminId}/propostas`),
          api.get(`/admin/clientes/${adminId}/contratos`),
        ]);
        setPropostas(p.data?.items || p.data || []);
        setContratos(c.data?.items || c.data || []);
      } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
      finally { setLoading(false); }
    })();
  }, [adminId]);

  if (loading) return <Card className="p-6 text-muted-foreground flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Carregando…</Card>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">Propostas ({propostas.length})</h3>
        {propostas.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-6 text-center border border-dashed rounded">
            Nenhuma proposta encontrada.
          </div>
        ) : (
          <ul className="divide-y text-sm">
            {propostas.slice(0, 20).map((p) => (
              <li key={p.id || p.numero} className="py-2" data-testid={`prop-${p.id || p.numero}`}>
                <div className="flex justify-between">
                  <span className="font-medium">{p.numero || p.id}</span>
                  <Badge variant="outline" className="text-xs">{p.status || "—"}</Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  {p.cliente_nome} · {fmtMoney(p.valor_total)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">Contratos ({contratos.length})</h3>
        {contratos.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-6 text-center border border-dashed rounded">
            Nenhum contrato encontrado.
          </div>
        ) : (
          <ul className="divide-y text-sm">
            {contratos.slice(0, 20).map((c) => (
              <li key={c.id} className="py-2" data-testid={`contr-${c.id}`}>
                <div className="flex justify-between">
                  <span className="font-mono text-xs">{c.id?.slice(0, 16)}…</span>
                  <Badge variant="outline" className="text-xs">{c.status || "—"}</Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  {c.cliente_nome} · {fmtMoney(c.valor_primeira_fatura)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// ============================================================
// 6. OPERAÇÕES (leitura simples — rotas por nome do cliente)
// ============================================================
function TabOperacoes({ nomeCliente }) {
  return (
    <Card className="p-5">
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <Map size={16} /> Operações & Roteirização
      </h3>
      <div className="text-sm text-muted-foreground">
        Para visualizar rotas, visitas e operações ligadas a <strong>{nomeCliente}</strong>, acesse a Busca Global
        ou o módulo de Roteirização.
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <a href={`/app/busca-global?q=${encodeURIComponent(nomeCliente)}`}
          className="text-xs px-3 py-1.5 rounded border hover:bg-zinc-50" data-testid="link-busca-operacoes">
          Buscar nas operações
        </a>
        <a href="/app/roteirizacao"
          className="text-xs px-3 py-1.5 rounded border hover:bg-zinc-50" data-testid="link-roteirizacao">
          Abrir Roteirização
        </a>
      </div>
    </Card>
  );
}

// ============================================================
// 7. HISTÓRICO
// ============================================================
function TabHistorico({ adminId }) {
  const [items, setItems] = useState([]);
  const [frota, setFrota] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, f] = await Promise.all([
          api.get(`/admin/clientes/${adminId}/historico`),
          api.get(`/admin/clientes/${adminId}/frota-historico`),
        ]);
        setItems(s.data?.items || s.data || []);
        setFrota(f.data?.items || []);
      } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
      finally { setLoading(false); }
    })();
  }, [adminId]);

  if (loading) return <Card className="p-6 text-muted-foreground flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Carregando…</Card>;

  const unified = [
    ...items.map((i) => ({ ...i, _origem: "status" })),
    ...frota.map((i) => ({ ...i, _origem: "frota/portal" })),
  ].sort((a, b) => String(b.criado_em || "").localeCompare(String(a.criado_em || "")));

  return (
    <Card className="p-5">
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <History size={16} /> Linha do tempo ({unified.length})
      </h3>
      {unified.length === 0 ? (
        <div className="text-sm text-muted-foreground italic py-6 text-center border border-dashed rounded">
          Nenhum evento registrado.
        </div>
      ) : (
        <ul className="space-y-2 text-sm">
          {unified.slice(0, 100).map((ev, idx) => (
            <li key={idx} className="border-l-2 border-orange-300 pl-3 py-1" data-testid={`hist-${idx}`}>
              <div className="flex justify-between gap-2">
                <span className="font-medium">{ev.acao || ev.evento || "—"}</span>
                <span className="text-xs text-muted-foreground">
                  {ev.criado_em ? new Date(ev.criado_em).toLocaleString("pt-BR") : ""}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                {ev.usuario_email || ev.alterado_por || "—"} · {ev._origem}
                {ev.motivo ? ` · ${ev.motivo}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
